import { useState, FormEvent, useMemo, useEffect } from "react";
import Form from "react-bootstrap/Form";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import differenceInMonths from "date-fns/differenceInMonths";
import { MONTH_DAY_YEAR_INPUT_DATE_FORMAT } from "@constants/date-formats";
import { resources } from "@constants/hardcoded-strings";
import { LayoutTypes } from "@enums/layout-types";
import { ButtonTypes } from "@enums/button-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { TimeRangeTypes } from "@enums/time-range-types";
import { SelectOption } from "@idw-types/common";
import { AxnCountry, SimpleAxnWorkflow } from "@idw-types/data/reference-data/axn";
import { WorkflowReportSearchFiltersState } from "@idw-types/state/workflow-report-state";
import { initialWorkflowReportState } from "@redux/reducers/workflow-report-reducer";
import { getTimeRange, getTimeRangeLabel } from "@utils/date-util";
import {
    mapWorkflowsToSelectOptionsWithStyle,
    mapWorkflowToSelectOptionWithStyle
} from "@utils/select-option-mapping-util";
import { sortStrings } from "@utils/string-util";
import { ButtonWithSpinner } from "../../../common/button-with-spinner/button-with-spinner";
import { SearchFormFilterRow } from "../../../common/search-form-filter-row/search-form-filter-row";
import { SelectFormGroup } from "../../../common/select-form-group/select-form-group";
import { TimeRangeSearchFormFilterRow } from "../../../common/time-range-search-form-filter-row/time-range-search-form-filter-row";
import { SearchFormHeaderContainer } from "../../../common/search-form-header-container/search-form-header-container";
import { ToggleFormGroup } from "../../../common/toggle-form-group/toggle-form-group";
import styles from "./workflow-report-search-form.less";

type WorkflowReportSearchFormProps = {
    requestInProgress: boolean;
    metadataRequestInProgress: boolean;
    workflows: Array<SimpleAxnWorkflow>;
    countries: Array<AxnCountry>;
    availableAppIds: Array<string>;
    availableCountries: Array<string>;
    searchFilters: WorkflowReportSearchFiltersState;
    handleSearch: (searchFilters: WorkflowReportSearchFiltersState) => void;
    handleMetadataSearch: (workflowId: string, startDate: Date, endDate: Date) => void;
};

type WorkflowReportSearchFormState = {
    selectedWorkflowOption: SelectOption;
    selectedAppIdOption: SelectOption;
    selectedCountryOption: SelectOption;
    selectedTimeRange: SelectOption;
    startDate: Date;
    endDate: Date;
    includeArchivedWorkflows: boolean;
};

export const WorkflowReportSearchForm = (props: WorkflowReportSearchFormProps) => {
    const maxDate = useMemo(() => new Date(), []);
    const minDate = useMemo(() => new Date(2021, 0, 1), []);
    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const {
        requestInProgress,
        metadataRequestInProgress,
        workflows,
        countries,
        availableAppIds,
        availableCountries,
        searchFilters,
        handleSearch,
        handleMetadataSearch
    } = props;

    const workflowsWithStatus: Array<SimpleAxnWorkflow> = useMemo(() => {
        return workflows.map(workflow => ({
            ...workflow,
            name: `${workflow.status === EntityStatusTypes.INACTIVE ? "(" + resources.common.archived + ") " : ""}${workflow.name}`
        }));
    }, [workflows]);

    const getFilterStateFromSearchFilters = (searchFilters: WorkflowReportSearchFiltersState): WorkflowReportSearchFormState => {
        let selectedWorkflow = workflowsWithStatus.find(workflow => searchFilters.workflowId === workflow.id);
        const selectedAppId = availableAppIds.find(appId => searchFilters.appId === appId);
        const selectedCountry = countries.find(country => searchFilters.country === country.alpha2);
        if (!searchFilters.includeArchivedWorkflows && selectedWorkflow && selectedWorkflow.status === EntityStatusTypes.INACTIVE) {
            selectedWorkflow = null;
        }

        return {
            selectedWorkflowOption: selectedWorkflow ? mapWorkflowToSelectOptionWithStyle(selectedWorkflow) : null,
            selectedAppIdOption: selectedAppId ? { label: selectedAppId, value: selectedAppId } : null,
            selectedCountryOption: selectedCountry ? { label: selectedCountry.name, value: selectedCountry.alpha2 } : null,
            selectedTimeRange: {
                label: getTimeRangeLabel(searchFilters.timeRangeType),
                value: searchFilters.timeRangeType
            },
            startDate: searchFilters.startDate,
            endDate: searchFilters.endDate,
            includeArchivedWorkflows: searchFilters.includeArchivedWorkflows
        };
    };

    const [showFilters, setShowFilters] = useState(true);
    const [filterState, setFilterState] = useState<WorkflowReportSearchFormState>(getFilterStateFromSearchFilters(searchFilters));

    useEffect(() => {
        setFilterState(getFilterStateFromSearchFilters(searchFilters));
    }, [searchFilters]);

    // run the metadata search if the workflow, start date or end date changes
    useEffect(() => {
        if (filterState.selectedWorkflowOption?.value) {
            handleMetadataSearch(filterState.selectedWorkflowOption.value, filterState.startDate, filterState.endDate);
        }
    }, [filterState.startDate, filterState.endDate, filterState.selectedWorkflowOption]);

    // update the app id and country dropdown if lists change
    useEffect(() => {
        let selectedAppIdOption = filterState.selectedAppIdOption;
        let selectedCountryOption = filterState.selectedCountryOption;

        if (
            filterState?.selectedAppIdOption?.value != null &&
            !availableAppIds.includes(filterState?.selectedAppIdOption?.value)
        ) {
            selectedAppIdOption = null;
        }

        if (
            filterState?.selectedCountryOption?.value != null &&
            !availableCountries.includes(filterState?.selectedCountryOption?.value)
        ) {
            selectedCountryOption = null;
        }

        setFilterState({
            ...filterState,
            selectedAppIdOption: selectedAppIdOption,
            selectedCountryOption: selectedCountryOption
        });
    }, [availableAppIds, availableAppIds]);

    const clearFilterState = () => {
        setFilterState(getFilterStateFromSearchFilters(initialWorkflowReportState.searchFilters));
    };

    const toggleShowFilters = () => {
        setShowFilters(!showFilters);
    };

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        const workflowId = filterState.selectedWorkflowOption?.value || null;
        const appId = filterState.selectedAppIdOption?.value || null;
        const country = filterState.selectedCountryOption?.value || null;

        handleSearch({
            workflowId,
            appId,
            country,
            timeRangeType: filterState.selectedTimeRange.value as TimeRangeTypes,
            startDate: filterState.startDate,
            endDate: filterState.endDate,
            includeArchivedWorkflows: filterState.includeArchivedWorkflows
        });
    };

    const handleUpdateWorkflowOption = (newValue: SelectOption) => {
        setFilterState({
            ...filterState,
            selectedWorkflowOption: newValue
        });
    };

    const handleUpdateAppIdOption = (newValue: SelectOption) => {
        setFilterState({
            ...filterState,
            selectedAppIdOption: newValue
        });
    };

    const handleUpdateCountryOption = (newValue: SelectOption) => {
        setFilterState({
            ...filterState,
            selectedCountryOption: newValue
        });
    };

    const handleTimeRangeChange = (newValue: SelectOption) => {
        if (newValue.value === TimeRangeTypes.CUSTOM) {
            setFilterState({
                ...filterState,
                selectedTimeRange: newValue
            });
        } else {
            setFilterState({
                ...filterState,
                selectedTimeRange: newValue,
                ...getTimeRange(newValue.value as TimeRangeTypes)
            });
        }
    };

    const handleStartDateChange = (newStartDate: Date) => {
        setFilterState({
            ...filterState,
            startDate: newStartDate
        });
    };

    const handleEndDateChange = (newEndDate: Date) => {
        setFilterState({
            ...filterState,
            endDate: newEndDate
        });
    };

    const handleIncludeArchivedWorkflowsChange = () => {
        let selectedWorkflowOption = filterState.selectedWorkflowOption;
        if (filterState.includeArchivedWorkflows && filterState.selectedWorkflowOption) {
            const workflow = workflowsWithStatus.find(workflow => workflow.id === filterState.selectedWorkflowOption.value);
            if (workflow?.status === EntityStatusTypes.INACTIVE) {
                selectedWorkflowOption = null;
            }
        }
        setFilterState({
            ...filterState,
            selectedWorkflowOption: selectedWorkflowOption,
            includeArchivedWorkflows: !filterState.includeArchivedWorkflows
        });
    };

    const noneSelectedOption: SelectOption = { label: "All", value: null };

    const workflowOptions: Array<SelectOption> = useMemo(() => {
        if (filterState.includeArchivedWorkflows) {
            return mapWorkflowsToSelectOptionsWithStyle(workflowsWithStatus).sort((a, b) => sortStrings(a.label, b.label));
        } else {
            const activeWorkflows = workflowsWithStatus.filter(workflow => workflow.status === EntityStatusTypes.ACTIVE);
            return mapWorkflowsToSelectOptionsWithStyle(activeWorkflows).sort((a, b) => sortStrings(a.label, b.label));
        }
    }, [workflowsWithStatus, filterState.includeArchivedWorkflows]);

    const appIdOptions: Array<SelectOption> = useMemo(() => {
        return [noneSelectedOption].concat(availableAppIds
            .filter(x => x)
            .map(appId => ({ label: appId, value: appId }))
            .sort((a, b) => sortStrings(a.label, b.label)));

    }, [availableAppIds]);

    const countryOptions: Array<SelectOption> = useMemo(() => {
        return [noneSelectedOption].concat(countries
            .filter(country => availableCountries.includes(country.alpha2))
            .map(country => ({ label: `${country.alpha2} - ${country.name}`, value: country.alpha2 }))
            .sort((a, b) => sortStrings(a.label, b.label)));
    }, [countries, availableCountries]);

    const timeRangeTypes = [
        TimeRangeTypes.TODAY,
        TimeRangeTypes.PAST_SEVEN_DAYS,
        TimeRangeTypes.CURRENT_MONTH,
        TimeRangeTypes.LAST_MONTH,
        TimeRangeTypes.CURRENT_QUARTER,
        TimeRangeTypes.LAST_QUARTER,
        TimeRangeTypes.CURRENT_YEAR,
        TimeRangeTypes.CUSTOM
    ];

    const isOlderThan6Months = useMemo(() => {
        return differenceInMonths(new Date(), filterState.startDate) > 5;
    }, [filterState.startDate]);

    return (
        <Form className={styles.workflowReportSearchForm} onSubmit={handleSubmit}>
            <SearchFormHeaderContainer
                idPrefix={"workflow-report"}
                showFilters={showFilters}
                showToggle={true}
                toggleShowFilters={toggleShowFilters}
            />
            {showFilters && (
                <>
                    {isOlderThan6Months && (
                        <Alert variant={"warning"}>{resources.pages.workflowReport.searchForm.sixMonthMessage}</Alert>
                    )}
                    <SearchFormFilterRow>
                        <SelectFormGroup
                            automationId={`workflow-report-search-filters-workflow-select`}
                            controlId={`workflow-report-search-filters-workflow-select`}
                            label={resources.common.searchForm.filters.workflow.label}
                            ariaLabel={resources.common.searchForm.filters.workflow.label}
                            onChange={handleUpdateWorkflowOption}
                            options={workflowOptions}
                            selectedOption={filterState.selectedWorkflowOption}
                            disabled={false}
                            isRequired={true}
                            isSearchable={true}
                            isClearable={false}
                            labelWidth={labelWidth}
                            layout={layout}
                        />
                        <SelectFormGroup
                            automationId={`workflow-report-search-filters-app-id-select`}
                            controlId={`workflow-report-search-filters-app-id-select`}
                            label={resources.pages.workflowReport.searchForm.filters.appId.label}
                            ariaLabel={resources.pages.workflowReport.searchForm.filters.appId.label}
                            onChange={handleUpdateAppIdOption}
                            options={appIdOptions}
                            selectedOption={filterState.selectedAppIdOption ? filterState.selectedAppIdOption : noneSelectedOption}
                            disabled={!filterState.selectedWorkflowOption || metadataRequestInProgress}
                            isLoading={metadataRequestInProgress}
                            isRequired={false}
                            isSearchable={true}
                            isClearable={false}
                            labelWidth={labelWidth}
                            layout={layout}
                        />
                        <SelectFormGroup
                            automationId={`workflow-report-search-filters-country-select`}
                            controlId={`workflow-report-search-filters-country-select`}
                            label={resources.pages.workflowReport.searchForm.filters.country.label}
                            ariaLabel={resources.pages.workflowReport.searchForm.filters.country.label}
                            onChange={handleUpdateCountryOption}
                            options={countryOptions}
                            selectedOption={filterState.selectedCountryOption ? filterState.selectedCountryOption : noneSelectedOption}
                            disabled={!filterState.selectedWorkflowOption || metadataRequestInProgress}
                            isLoading={metadataRequestInProgress}
                            isRequired={false}
                            isSearchable={true}
                            isClearable={false}
                            labelWidth={labelWidth}
                            layout={layout}
                        />
                    </SearchFormFilterRow>
                    <TimeRangeSearchFormFilterRow
                        idPrefix={"workflow-report-search-filters"}
                        labelWidth={labelWidth}
                        layout={layout}
                        onChangeTimeRange={handleTimeRangeChange}
                        onChangeStartDate={handleStartDateChange}
                        onChangeEndDate={handleEndDateChange}
                        selectedTimeRange={filterState.selectedTimeRange}
                        timeRangeTypes={timeRangeTypes}
                        startDateLabel={resources.common.searchForm.filters.startDate.label}
                        startDateAriaLabel={resources.common.searchForm.filters.startDate.label}
                        endDateLabel={resources.common.searchForm.filters.endDate.label}
                        endDateAriaLabel={resources.common.searchForm.filters.endDate.label}
                        minDate={minDate}
                        maxDate={maxDate}
                        dateFormat={MONTH_DAY_YEAR_INPUT_DATE_FORMAT}
                        startDate={filterState.startDate}
                        endDate={filterState.endDate}
                        showMonthDropdown={true}
                        showYearDropdown={true}
                        showMonthYearPicker={false}
                    />
                    <SearchFormFilterRow>
                        <ToggleFormGroup
                            controlId={"workflow-report-search-filters-include-archived-workflows-toggle"}
                            automationId={"workflow-report-search-filters-include-archived-workflows-toggle"}
                            className={styles.includeArchivedToggleFormGroup}
                            label={resources.pages.workflowReport.searchForm.filters.includeArchivedWorkflows.label}
                            ariaLabel={resources.pages.workflowReport.searchForm.filters.includeArchivedWorkflows.label}
                            value={`${filterState.includeArchivedWorkflows}`}
                            disabled={false}
                            isRequired={false}
                            labelWidth={labelWidth}
                            onChange={handleIncludeArchivedWorkflowsChange}
                            checked={filterState.includeArchivedWorkflows}
                            checkedIcon={false}
                            uncheckedIcon={false}
                        />
                    </SearchFormFilterRow>
                    <div className={styles.buttonDiv}>
                        <Button id="workflow-report-search-clear-button" variant={"outline-secondary"} onClick={clearFilterState}>{resources.common.buttons.clear.label}</Button>
                        <ButtonWithSpinner
                            id="workflow-report-search-load-button"
                            disabled={requestInProgress}
                            hideSpinner={false}
                            variant="primary"
                            type={ButtonTypes.SUBMIT}
                            label={resources.common.buttons.load.label}
                        />
                    </div>
                </>
            )}
        </Form>
    );
};